import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Heart, Coins, Calendar, Moon, Bookmark, Check, Loader2, WifiOff, Lock, Star, ChevronRight, MessageCircle, Flame } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import AskStarsBanner from "@/components/AskStarsBanner";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import Constellation from "@/components/Constellation";
import AdBanner from "@/components/AdBanner";
import IntroOffer from "@/components/IntroOffer";
import OfferBanner from "@/components/OfferBanner";
import RetroBanner from "@/components/RetroBanner";
import { TodayExplore } from "@/components/Explore";
import ShareAstra from "@/components/ShareAstra";
import BadgeCelebration from "@/components/BadgeCelebration";

const PREMIUM_META = {
  love: { label: "Love Focus", icon: Heart, testId: "premium-teaser-love" },
  money: { label: "Money", icon: Coins, testId: "premium-teaser-money" },
  weekly: { label: "Weekly Reading", icon: Calendar, testId: "premium-teaser-weekly" },
  monthly: { label: "Monthly Reading", icon: Moon, testId: "premium-teaser-monthly" },
};

function splitSentences(text) {
  if (!text) return [];
  const parts = text.match(/[^.!?]+[.!?]+(\s|$)|[^.!?]+$/g);
  return parts ? parts.map((s) => s.trim()).filter(Boolean) : [text];
}

// Graduated reveal: line 1 full, then progressively faded (opacity only; blur on inline text glitches in iOS WebKit).
function revealStyle(i) {
  return { opacity: [1, 0.75, 0.4, 0.15][i] ?? 0.06 };
}

function PremiumTeaser({ k, text, isPremium, onUnlock, fromLabel }) {
  const meta = PREMIUM_META[k];
  const Icon = meta.icon;
  const sentences = splitSentences(text);
  return (
    <div
      data-testid={meta.testId}
      onClick={() => !isPremium && onUnlock(meta.label)}
      className={`relative overflow-hidden bg-[#0F1523] border border-[#D4AF37]/15 rounded-2xl p-5 ${
        !isPremium ? "cursor-pointer hover:border-[#D4AF37]/40 transition-colors" : ""
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon size={17} className="gold-text" />
          <h3 className="text-xl font-medium">{meta.label}</h3>
        </div>
        {!isPremium && <Lock size={14} className="text-[#A7B0C0]" />}
      </div>

      {isPremium ? (
        <p className="text-base leading-relaxed text-[#D6D0C5]">{text}</p>
      ) : (
        <div className="relative">
          <p className="text-base leading-relaxed text-[#D6D0C5] select-none">
            {sentences.map((s, i) => (
              <span key={i} style={revealStyle(i)}>
                {s}{" "}
              </span>
            ))}
          </p>
          <div className="mt-3 flex items-center justify-center gap-1.5">
            <Lock size={13} className="gold-text" />
            <span className="text-sm gold-text tracking-wide">Unlock the full reading · from {fromLabel}/mo</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Today({ onSubscribe }) {
  const { user } = useAuth();
  const { prices } = useCurrency();
  const [data, setData] = useState(null);
  const [offline, setOffline] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [failed, setFailed] = useState(false);
  const [params, setParams] = useSearchParams();
  const glowRef = useRef(null);
  const [celebrate, setCelebrate] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    const cacheKey = `astra_today_${today}`;
    (async () => {
      try {
        const { data } = await api.get("/readings/today");
        setData(data);
        localStorage.setItem(cacheKey, JSON.stringify(data));
      } catch {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          setData(JSON.parse(cached));
          setOffline(true);
        } else {
          setFailed(true);
        }
      }
    })();
  }, [attempt]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/readings/save");
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!data || params.get("glow") !== "1") return;
    glowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    const t = setTimeout(() => setParams({}, { replace: true }), 5500);
    return () => clearTimeout(t);
  }, [data, params, setParams]);

  useEffect(() => {
    const b = data?.streak?.new_badge;
    if (b && sessionStorage.getItem("astra_badge_seen") !== `${b}-${data.date}`) {
      sessionStorage.setItem("astra_badge_seen", `${b}-${data.date}`);
      setCelebrate(b);
    }
  }, [data?.streak?.new_badge, data?.date]);

  useEffect(() => {
    if (data?.streak?.rewarded_today) {
      toast.success(`${data.streak.count}-day streak!`, { description: "You've earned a free Astra Interactive question." });
    }
  }, [data?.streak?.rewarded_today, data?.streak?.count]);

  if (!data && failed) {
    return (
      <div data-testid="today-error" className="flex flex-col items-center justify-center py-32 px-6 text-center text-[#A7B0C0]">
        <p className="italic mb-4">The stars are a little cloudy right now. Please try again in a moment.</p>
        <button
          data-testid="btn-retry-today"
          onClick={() => { setFailed(false); setAttempt((n) => n + 1); }}
          className="border border-[#D4AF37]/40 gold-text px-6 py-2.5 rounded-full hover:border-[#D4AF37] transition-colors"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-[#A7B0C0]">
        <Loader2 className="animate-spin gold-text mb-3" size={28} />
        <p className="italic">Reading the stars for you…</p>
      </div>
    );
  }

  const r = data.reading;
  const glow = params.get("glow") === "1";
  const isPremium = user?.is_premium;
  const fromLabel = `${prices.symbol}${prices.annualPerMonth}`;

  return (
    <div className="px-5 pt-3 pb-28 fade-up">
      {celebrate && <BadgeCelebration badge={celebrate} onClose={() => setCelebrate(null)} />}
      <IntroOffer onSubscribe={onSubscribe} />
      <div className="flex flex-col items-center text-center">
        <Constellation sign={data.sun_sign} size={48} className="-my-1.5" />
        <p className="text-[10px] uppercase tracking-[0.25em] gold-text">
          {new Date(data.date + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
        </p>
        <h1 className="text-[1.7rem] leading-tight font-light">Your {data.sun_sign} Sky</h1>
        <div className="flex items-center gap-2.5 mt-0.5 text-xs text-[#A7B0C0]">
          <span>Mood · <span className="text-[#E8DCC4]">{r.mood}</span></span>
          <span className="w-1 h-1 rounded-full bg-[#D4AF37]/50" />
          <span>Lucky · <span className="text-[#E8DCC4]">{r.lucky_color}</span></span>
        </div>
        {offline && (
          <span className="flex items-center gap-1 text-xs text-[#A7B0C0] mt-2">
            <WifiOff size={12} /> Cached for offline contemplation
          </span>
        )}
      </div>

      {/* Free daily reading */}
      {data.streak && <StreakRow s={data.streak} />}

      <div data-testid="daily-reading-card" ref={glowRef} className={`relative bg-[#0F1523] border border-[#D4AF37]/20 rounded-2xl px-5 pt-4 pb-3.5 mt-2.5 ${glow ? "arrive-glow" : ""}`}>
        <p className="text-[11px] uppercase tracking-[0.25em] gold-text mb-2">Today's Reading</p>
        <p className="text-base leading-relaxed text-[#D6D0C5]">{r.overall}</p>
        <button
          data-testid="btn-save-reading"
          onClick={save}
          disabled={saving || saved}
          className="mt-2.5 flex items-center gap-2 text-sm gold-text disabled:opacity-60"
        >
          {saved ? <><Check size={15} /> Saved to archive</> : saving ? <><Loader2 size={15} className="animate-spin" /> Saving…</> : <><Bookmark size={15} /> Save to archive</>}
        </button>
      </div>

      <AskStarsBanner to="/ask" testId="link-ask-stars-top" showFree={!isPremium} className="mt-4" />
      {!user?.is_demo && <div className="mt-4"><OfferBanner isPremium={isPremium} /></div>}
      <RetroBanner className="mt-4" />
      <TodayExplore sign={data.sun_sign} />

      {/* Ad (free users only; Premium is ad-free) */}
      <AdBanner slot="3530801375" onSubscribe={onSubscribe} className="mt-4" />

      {/* Premium teasers */}
      <div className="space-y-3 mt-4 mb-4">
        {Object.keys(PREMIUM_META).map((k) => (
          <PremiumTeaser key={k} k={k} text={r[k]} isPremium={isPremium} onUnlock={onSubscribe} fromLabel={fromLabel} />
        ))}
        <Link
          to="/ask"
          data-testid="link-ask-stars"
          className="flex items-center justify-between bg-[#0F1523] border border-[#D4AF37]/30 hover:border-[#D4AF37]/60 rounded-2xl px-5 py-4 transition-colors"
        >
          <span className="flex items-center gap-2 text-[#E8DCC4]">
            <MessageCircle size={16} className="gold-text shrink-0" />
            <span>Ask the stars with <span className="gold-text">Astra Interactive</span>{!isPremium && " – try it now for free"}</span>
          </span>
          <ChevronRight size={16} className="gold-text" />
        </Link>
        <Link
          to="/signs"
          data-testid="link-all-signs"
          className="flex items-center justify-between bg-[#0F1523] border border-[#D4AF37]/30 hover:border-[#D4AF37]/60 rounded-2xl px-5 py-4 transition-colors"
        >
          <span className="flex items-center gap-2 text-[#E8DCC4]">
            <Star size={16} className="gold-text" /> Read all 12 star sign readings
          </span>
          {isPremium ? <ChevronRight size={16} className="gold-text" /> : <Lock size={14} className="text-[#A7B0C0]" />}
        </Link>
      </div>

      {!user?.is_demo && <ShareAstra className="mb-4" />}

      {!isPremium && (
        <button
          onClick={() => onSubscribe("your full daily reading")}
          className="w-full mb-4 bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium text-lg py-3.5 rounded-full transition-colors"
          data-testid="btn-unlock-premium"
        >
          Unlock everything · from {fromLabel}/mo
        </button>
      )}
    </div>
  );
}

function StreakRow({ s }) {
  const filled = s.count % s.every || (s.count ? s.every : 0);
  return (
    <div data-testid="streak-row" className="mt-2.5 bg-[#0F1523] border border-[#D4AF37]/15 rounded-2xl px-4 py-2.5">
      <div className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-1.5 text-starlight">
          <Flame size={15} className="gold-text" />
          <span data-testid="streak-count">{s.count}-day streak</span>
        </span>
        <span data-testid="streak-next" className="text-xs text-[#A7B0C0]">
          {s.rewarded_today ? "Free question earned today" : `${s.to_reward} more day${s.to_reward === 1 ? "" : "s"} to a free question`}
        </span>
      </div>
      <div className="flex gap-1 mt-2">
        {Array.from({ length: s.every }).map((_, i) => (
          <span key={i} className={`h-1 flex-1 rounded-full ${i < filled ? "bg-[#D4AF37]" : "bg-[#D4AF37]/15"}`} />
        ))}
      </div>
    </div>
  );
}
